import { createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { createSelector } from "reselect";
import { getAccessToken } from "../../services/authTokenStore";

export const userSlice = createSlice({
  name: 'user',
  initialState: {
    isLoggingOut: false,
    tenantName: '',
    clientId: [],
    clientAccountId: [],
    hasAccessToAllClients: false,
    permissionsLoaded: false,
    userProfile: {}
  },
  reducers: {
    logOut: state => {
      state.isLoggingOut = true;
      state.userProfile = {}; 
    },
    setUserProfile: (state, action) => {
      if (!isEqual(state.userProfile, action.payload)) {
        state.userProfile = action.payload;
      }
    },
    setTenantName: (state, action) => {
      if (!isEqual(state.tenantName, action.payload)) {
        state.tenantName = action.payload;
      }
    },
    setClientId: (state, action) => {
      if (!isEqual(state.clientId, action.payload)) {
        state.clientId = action.payload;
      }
    },
    setClientAccountId: (state, action) => {
      if (!isEqual(state.clientAccountId, action.payload)) {
        state.clientAccountId = action.payload;
      }
    },
    setHasAccessToAllClients: (state, action) => {
      if (!isEqual(state.hasAccessToAllClients, action.payload)) {
        state.hasAccessToAllClients = action.payload;
      }
    },
    setPermissionsLoaded: (state, action) => {
      if (!isEqual(state.permissionsLoaded, action.payload)) {
        state.permissionsLoaded = action.payload;
      }
    }
  },
});

export const selectIsSaasManager = (state) => state?.user?.userProfile?.role === "saas_manager";
export const selectIsPmfSupport = (state) => state?.user?.userProfile?.role === "pmf_support";
export const selectIsAdmin = (state) => ["admin", "owner", "account_manager", "mechanic", "supervisor"].includes(state?.user?.userProfile?.role || "");
export const selectIsAdminOnly = (state) => state?.user?.userProfile?.role === "admin";
export const selectIsOwner = (state) => state?.user?.userProfile?.role === "owner";
export const selectIsClient = (state) => state?.user?.userProfile?.role === "client";
export const selectIsAccountManager = (state) => state?.user?.userProfile?.role === "account_manager";
export const selectIsSupervisor = (state) => state?.user?.userProfile?.role === "supervisor";
export const selectIsMechanic = (state) => state?.user?.userProfile?.role === "mechanic";

export const selectUserEmail = (state) => state?.user?.userProfile?.email || "";
export const selectUserRole = (state) => state?.user?.userProfile?.role || "";
export const selectUserLotId = (state) => state?.user?.userProfile?.lotId || "";
export const selectUserPermissions = (state) => state?.user?.userProfile?.permission || [];
export const selectUserId = (state) => state?.user?.userProfile?.sub || "";
export const selectIsLoggedIn = () => !!getAccessToken();

export const selectIsSeamless = (state) => !getAccessToken() && !!state?.seamlessToken?.token;

export const selectIsLoggingOut = (state) => state?.user?.isLoggingOut;
export const selectPermissionsLoaded = (state) => state?.user?.permissionsLoaded;

export const selectUserClientAccountId = createSelector(
  (state) => state?.user?.clientAccountId,
  (clientAccountId) => clientAccountId ?? []
);

export const selectTenantName = createSelector(
  (state) => state?.user?.tenantName,
  (tenantName) => tenantName ?? []
);

export const selectUserClientId = createSelector(
  (state) => state?.user?.clientId,
  (clientId) => clientId ?? []
);

export const userHasOneClient = createSelector(
  selectUserClientId,
  (clientId) => clientId.length === 1
);

export const userHasOneClientAccount = createSelector(
  selectUserClientAccountId,
  (clientAccountId) => clientAccountId.length === 1
);

export const hasAccessToAllClients = createSelector(
  (state) => state?.user?.hasAccessToAllClients,
  (hasAccessToAllClients) => hasAccessToAllClients ?? []
);

export const selectRole = (state) => state.user.userProfile.role || "";

export const {
  logOut,
  setUserProfile,
  setTenantName,
  setClientId,
  setClientAccountId,
  setHasAccessToAllClients,
  setPermissionsLoaded
} = userSlice.actions;

export default userSlice.reducer;
